import { useEffect, useRef, useState } from 'react'
import type { CharacterSheet, RollEntry, RollReason } from '@rollcall/contracts'
import { ABILITY_KEYS, ABILITY_NAMES } from '@rollcall/rules'
import type { RollMode } from '@rollcall/rules'
import { useT } from './i18n-context.js'
import { contentName } from './labels.js'
import { RollLog, RollOutcomeLine, reasonLabel } from './rolls.js'

/**
 * Роллер отдельной панелью (D12).
 *
 * Раньше журнал бросков стоял внизу вкладки: бросив с верха экрана,
 * результат приходилось искать прокруткой, а переключатель
 * преимущества относился только к атакам оружием и на свой бросок
 * не действовал.
 *
 * Панель открывается с любой вкладки листа и устроена сверху вниз
 * так, как ею пользуются: результат последнего броска, потом
 * переключатель преимущества — общий на все броски панели, — потом
 * то, по чему бросают, и своя запись с ручным вводом. Журнал —
 * в конце: он нужен реже, чем последний результат.
 *
 * Ни одного расчёта здесь нет: прибавки собирает сервер по величине
 * листа (D3), а признаки листа сами ставят преимущество или помеху —
 * в записи журнала видно, из-за чего.
 */

export type RollOptions = {
  notation?: string | null
  manual?: number | null
  mode?: RollMode
}

export type RollPanelProps = {
  readonly sheet: CharacterSheet
  readonly rolls: readonly RollEntry[]
  readonly busy: boolean
  readonly failure: string | null
  readonly onRoll: (reason: RollReason, options: RollOptions) => void
  readonly onClose: () => void
  /**
   * Последний бросок целиком: причина и запись.
   *
   * Целиком, а не одной причиной: у своего броска запись — часть
   * броска, и повтор без неё сервер отклоняет.
   */
  readonly last: { reason: RollReason; notation: string | null } | null
  /** Преимущество и помеха: один переключатель на лист, а не на панель. */
  readonly mode: RollMode
  readonly onMode: (mode: RollMode) => void
  /**
   * Скрытый бросок (D12): виден мастеру и только ему, а в общем
   * журнале остаётся отметка, что бросок был.
   *
   * Переключатель показывается лишь тому, кто вправе им пользоваться:
   * игроку он не нужен, а сервер всё равно проверит, что скрывает
   * мастер этой кампании.
   */
  readonly canHide?: boolean | undefined
  readonly hidden?: boolean | undefined
  readonly onHidden?: ((hidden: boolean) => void) | undefined
}

const MODES = ['normal', 'advantage', 'disadvantage'] as const

export function RollPanel({
  sheet,
  rolls,
  busy,
  failure,
  onRoll,
  onClose,
  last: lastRoll,
  mode,
  onMode,
  canHide = false,
  hidden = false,
  onHidden,
}: RollPanelProps) {
  const t = useT()
  const [notation, setNotation] = useState('1d20')
  const [manual, setManual] = useState('')
  const body = useRef<HTMLDivElement>(null)

  const last = rolls[0]

  // Новый бросок возвращает панель к началу: результат стоит первым,
  // и искать его прокруткой — ровно то, из-за чего роллер и переехал
  // из подвала вкладки в панель.
  useEffect(() => {
    body.current?.scrollTo?.({ top: 0 })
  }, [last?.id])

  return (
    <section
      className="app-roll-panel"
      role="dialog"
      aria-modal="false"
      aria-labelledby="roll-panel-title"
      data-testid="roll-panel"
    >
      <div className="app-roll-panel-head">
        <h2 id="roll-panel-title">{t('roll.panel')}</h2>
        <button
          type="button"
          className="app-icon-button"
          aria-label={t('roll.close')}
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="app-roll-panel-body" ref={body}>
        {/* Последний результат — первым и без прокрутки: ради него
            панель и открывают. */}
        <section aria-labelledby="roll-last" className="app-roll-last">
          <h3 id="roll-last">{t('roll.last')}</h3>
          {last === undefined ? (
            <p>{t('roll.empty')}</p>
          ) : (
            <div data-testid="roll-last">
              <p className="app-value-label">{reasonLabel(t, last.reason)}</p>
              <RollOutcomeLine outcome={last.outcome} />
            </div>
          )}
          {failure !== null && (
            <p className="app-problems" role="alert">
              {failure}
            </p>
          )}
        </section>

        {/* Преимущество и помеха — обстоятельство броска, а не свойство
            того, по чему бросают: переключатель один на всю панель. */}
        <fieldset className="app-slot">
          <legend className="app-slot-legend">{t('roll.mode')}</legend>
          <div className="app-slot-options">
            {MODES.map((item) => (
              <label key={item} className="app-option">
                <input
                  type="radio"
                  name="panel-roll-mode"
                  value={item}
                  checked={mode === item}
                  onChange={() => {
                    onMode(item)
                  }}
                />
                <span>{t(`roll.${item}`)}</span>
              </label>
            ))}
          </div>
          <p className="app-slot-hint">{t('roll.modeHint')}</p>
        </fieldset>

        {canHide && (
          <fieldset className="app-slot">
            <legend className="app-slot-legend">{t('roll.hidden')}</legend>
            <label className="app-option">
              <input
                type="checkbox"
                checked={hidden}
                onChange={(event) => {
                  onHidden?.(event.target.checked)
                }}
              />
              <span>{t('roll.hiddenLabel')}</span>
            </label>
            <p className="app-slot-hint">{t('roll.hiddenHint')}</p>
          </fieldset>
        )}

        <Again roll={lastRoll} busy={busy} mode={mode} onRoll={onRoll} />

        <section aria-labelledby="roll-by">
          <h3 id="roll-by">{t('roll.by')}</h3>

          <Group title={t('sheet.abilities')}>
            {ABILITY_NAMES.map((ability) => (
              <RollButton
                key={ability}
                label={t(`abilities.${ability}`)}
                value={sheet.values[`check.${ABILITY_KEYS[ability]}`]?.value}
                busy={busy}
                onRoll={() => {
                  onRoll(
                    { kind: 'check', subject: `check.${ABILITY_KEYS[ability]}` },
                    { mode },
                  )
                }}
              />
            ))}
          </Group>

          <Group title={t('sheet.saves')}>
            {sheet.saves.map((save) => (
              <RollButton
                key={save.path}
                label={t(`abilities.${save.ability}`)}
                value={sheet.values[save.path]?.value}
                busy={busy}
                onRoll={() => {
                  onRoll({ kind: 'save', subject: save.path }, { mode })
                }}
              />
            ))}
          </Group>

          <Group title={t('sheet.skills')} scroll>
            {sheet.skills.map((skill) => (
              <RollButton
                key={skill.path}
                label={contentName(t, skill.id, skill.name)}
                value={sheet.values[skill.path]?.value}
                busy={busy}
                onRoll={() => {
                  onRoll({ kind: 'check', subject: skill.path }, { mode })
                }}
              />
            ))}
          </Group>
        </section>

        <section aria-labelledby="roll-own">
          <h3 id="roll-own">{t('roll.reason.custom')}</h3>
          <label className="app-field">
            <span>{t('roll.notation')}</span>
            <input
              type="text"
              inputMode="text"
              value={notation}
              onChange={(event) => {
                setNotation(event.target.value)
              }}
            />
          </label>
          <p className="app-slot-hint">{t('roll.notationHint')}</p>
          <div className="app-actions">
            <button
              type="button"
              className="app-button"
              disabled={busy}
              onClick={() => {
                onRoll({ kind: 'custom', subject: null }, { notation, mode })
              }}
            >
              {t('roll.roll')}
            </button>
          </div>

          {/* Ручной ввод равноправен (D12): человек бросил настоящие
              кости и внёс число — оно ложится в тот же журнал. */}
          <label className="app-field app-field-narrow">
            <span>{t('roll.manual')}</span>
            <input
              type="number"
              inputMode="numeric"
              value={manual}
              onChange={(event) => {
                setManual(event.target.value)
              }}
            />
          </label>
          <p className="app-slot-hint">{t('roll.manualHint')}</p>
          <div className="app-actions">
            <button
              type="button"
              className="app-button app-button-quiet"
              disabled={busy || manual === ''}
              onClick={() => {
                onRoll(
                  { kind: 'custom', subject: null },
                  { notation, manual: Number(manual) },
                )
                setManual('')
              }}
            >
              {t('roll.manualAdd')}
            </button>
          </div>
        </section>

        {/* Тот же повтор ещё раз, над журналом: до нижнего края
            панели прокручивать каждый раз — ровно то неудобство,
            из-за которого роллер и переехал в панель. */}
        <Again roll={lastRoll} busy={busy} mode={mode} onRoll={onRoll} />

        <RollLog entries={rolls} />
      </div>
    </section>
  )
}

/**
 * Повтор последнего броска.
 *
 * Повторяется весь бросок, а не его причина: запись своего броска
 * уходит вместе с ним, иначе сервер отвечает «проверьте запись»
 * на броске, который только что прошёл.
 *
 * Ручной ввод не повторяется намеренно: «внести своё число» —
 * не бросок, и повторять там нечего.
 */
function Again({
  roll,
  busy,
  mode,
  onRoll,
}: {
  readonly roll: { reason: RollReason; notation: string | null } | null
  readonly busy: boolean
  readonly mode: RollMode
  readonly onRoll: (reason: RollReason, options: RollOptions) => void
}) {
  const t = useT()
  if (roll === null) return null
  return (
    <div className="app-actions">
      <button
        type="button"
        className="app-button"
        disabled={busy}
        onClick={() => {
          onRoll(roll.reason, { mode, notation: roll.notation })
        }}
      >
        {t('roll.again', { subject: reasonLabel(t, roll.reason) })}
      </button>
    </div>
  )
}

function Group({
  title,
  scroll = false,
  children,
}: {
  readonly title: string
  readonly scroll?: boolean
  readonly children: React.ReactNode
}) {
  return (
    <div className="app-roll-group">
      <h4 className="app-group-title">{title}</h4>
      {/* Восемнадцать навыков прокручиваются внутри своего списка,
          а не уносят страницу вниз. */}
      <div className={scroll ? 'app-roll-choices app-scroll-list' : 'app-roll-choices'}>
        {children}
      </div>
    </div>
  )
}

function RollButton({
  label,
  value,
  busy,
  onRoll,
}: {
  readonly label: string
  readonly value: number | readonly string[] | undefined
  readonly busy: boolean
  readonly onRoll: () => void
}) {
  const number = typeof value === 'number' ? value : null
  return (
    <button type="button" className="app-value-button" disabled={busy} onClick={onRoll}>
      <span className="app-value-label">{label}</span>
      <span className="app-value-number">
        {number === null ? '' : number >= 0 ? `+${number}` : `−${Math.abs(number)}`}
      </span>
    </button>
  )
}
